import { getJson } from './httpClient';
import { normalizeRestaurantApiFields } from '../../utils/apiValues';

/**
 * Maps All Restaurants sort controls to `sort_by` on GET /restaurants/filter/{radius}km
 * (API echoes this in `data.filters.sort_by`, e.g. `newest`).
 *
 * @param {'asc'|'desc'|string|null|undefined} sort
 * @returns {string|null}
 */
function mapDistanceFilterSortBy(sort) {
  const s = String(sort || '').trim().toLowerCase();
  if (s === 'desc') return 'newest';
  if (s === 'asc') return 'oldest';
  return s ? s : null;
}

/**
 * Path segment for restaurant detail routes (`/restaurant/:slug`).
 *
 * @param {{ id?: string|number, slug?: string|null }} restaurant
 * @returns {string}
 */
export function restaurantDetailPath(restaurant) {
  const slug = String(restaurant?.slug ?? '').trim();
  if (slug) {
    return `/restaurant/${encodeURIComponent(slug)}`;
  }
  const id = String(restaurant?.id ?? '').trim();
  return id ? `/restaurant/${encodeURIComponent(id)}` : '#';
}

/**
 * Cuisine tag on a restaurant (from GET /getRestaurants).
 * @typedef {Object} RestaurantCuisine
 * @property {string} id
 * @property {string} name
 * @property {string} [image_url]
 */

/**
 * Single restaurant item as returned by GET /getRestaurants.
 * @typedef {Object} Restaurant
 * @property {string} id
 * @property {string} [slug]
 * @property {string} name
 * @property {string} [description]
 * @property {string} [logo_url]
 * @property {string} [dark_logo_url]
 * @property {string} [banner_url]
 * @property {string|boolean|number} [is_restaurant_of_the_day] API may send "" or "1"
 * @property {string} [pick_of_the_day_banner_url]
 * @property {RestaurantCuisine[]} [cuisines]
 * @property {string} [delivery_time] e.g. `"1802 minutes"` (may mirror nearest branch)
 * @property {{ delivery_time?: string, distance?: string, branch_distance_km?: string }[]} [branches]
 */

/**
 * Full API envelope for GET /getRestaurants.
 * @typedef {Object} GetRestaurantsResponse
 * @property {boolean} success
 * @property {string} message
 * @property {{ restaurants?: Restaurant[], is_logged_in?: string }} data
 */

/**
 * Fetches the public list of restaurants.
 * Endpoint (dev proxy): /api/v1/getRestaurants
 *
 * @param {{
 *  latitude?: number|string,
 *  longitude?: number|string,
 *  page?: number|string,
 *  perPage?: number|string,
 *  isRestaurantOfTheDay?: boolean,
 *  sort?: 'asc'|'desc'|string|null,
 * } & RequestInit} [opts]
 * @returns {Promise<{ restaurants: Restaurant[], message: string }>}
 */
export async function fetchRestaurants(opts = {}) {
  // Backwards compatible with old signature where we passed only RequestInit.
  const { latitude, longitude, page, perPage, isRestaurantOfTheDay, sort, ...fetchInit } = opts || {};

  const qs = new URLSearchParams();
  if (latitude !== undefined && latitude !== null && String(latitude).trim() !== '') {
    qs.set('latitude', String(latitude));
  }
  if (longitude !== undefined && longitude !== null && String(longitude).trim() !== '') {
    qs.set('longitude', String(longitude));
  }
  if (page !== undefined && page !== null && String(page).trim() !== '') {
    qs.set('page', String(page));
  }
  if (perPage !== undefined && perPage !== null && String(perPage).trim() !== '') {
    // Support both common param names.
    qs.set('per_page', String(perPage));
  }
  if (isRestaurantOfTheDay) {
    qs.set('is_restaurant_of_the_day', 'true');
  }
  if (sort) {
    qs.set('sort', String(sort));
  }

  const path = qs.toString() ? `/getRestaurants?${qs.toString()}` : '/getRestaurants';
  /** @type {GetRestaurantsResponse} */
  const json = await getJson(path, fetchInit);

  if (!json.success) {
    throw new Error(json.message || 'Restaurants request was not successful');
  }

  const restaurants = json.data?.restaurants;
  if (!Array.isArray(restaurants)) {
    throw new Error('Unexpected API shape: data.restaurants is not an array');
  }

  return {
    restaurants,
    message: json.message,
  };
}

/**
 * If the list includes a "restaurant of the day" flag, keep only those rows.
 * If nothing matches, returns the original list (server may have filtered already).
 *
 * @param {Restaurant[]} restaurants
 * @returns {Restaurant[]}
 */
export function narrowToRestaurantOfTheDay(restaurants) {
  if (!Array.isArray(restaurants) || !restaurants.length) return restaurants;
  const isMarked = (r) => {
    if (!r) return false;
    const v = r.is_restaurant_of_the_day ?? r.isRestaurantOfTheDay;
    if (v === true || v === 1) return true;
    if (typeof v === 'string') {
      const lower = v.trim().toLowerCase();
      return lower === 'true' || lower === 'yes' || lower === '1' || lower === 'on';
    }
    return false;
  };
  const hits = restaurants.filter(isMarked);
  return hits.length ? hits : restaurants;
}

/**
 * Fetch restaurants for a specific cuisine.
 * Endpoint: /cuisines/:id/restaurants?latitude=...&longitude=...
 *
 * @param {string|number} cuisineId
 * @param {{
 *  latitude?: number|string,
 *  longitude?: number|string,
 * } & RequestInit} [opts]
 * @returns {Promise<{ restaurants: Restaurant[], message: string }>}
 */
export async function fetchRestaurantsByCuisine(cuisineId, opts = {}) {
  const id = encodeURIComponent(String(cuisineId ?? '').trim());
  if (!id) throw new Error('Missing cuisine id');

  const { latitude, longitude, ...fetchInit } = opts || {};
  const qs = new URLSearchParams();
  if (latitude !== undefined && latitude !== null && String(latitude).trim() !== '') {
    qs.set('latitude', String(latitude));
  }
  if (longitude !== undefined && longitude !== null && String(longitude).trim() !== '') {
    qs.set('longitude', String(longitude));
  }

  const path = `/cuisines/${id}/restaurants${qs.toString() ? `?${qs.toString()}` : ''}`;
  const json = await getJson(path, fetchInit);

  if (json && json.success === false) {
    throw new Error(json.message || 'Cuisine restaurants request was not successful');
  }

  const restaurants =
    (json && json.data && Array.isArray(json.data.restaurants) && json.data.restaurants) ||
    (json && json.data && Array.isArray(json.data.data) && json.data.data) ||
    (json && Array.isArray(json.restaurants) && json.restaurants) ||
    (json && Array.isArray(json.data) && json.data) ||
    null;

  if (!Array.isArray(restaurants)) {
    throw new Error('Unexpected API shape: restaurants is not an array');
  }

  return {
    restaurants,
    message: json?.message || '',
  };
}

/**
 * Filtered restaurants endpoint.
 * Supports distance route, sorting, and restaurant-of-the-day together (query matches API `data.filters`).
 *
 * Examples:
 * - /restaurants/filter/10km?lat=...&lng=...&sort_by=newest&restaurant_of_the_day=1
 *
 * When there is no radius but you only need restaurant-of-the-day, use {@link fetchRestaurants}.
 *
 * **Latitude and longitude are required** (as `lat` / `lng` query params) for distance filtering and sorting.
 *
 * @param {{
 *  distance?: string|null,
 *  sort?: 'asc'|'desc'|string|null,
 *  isRestaurantOfTheDay?: boolean,
 *  latitude?: number|string|null,
 *  longitude?: number|string|null,
 * } & RequestInit} [opts]
 * @returns {Promise<{ restaurants: Restaurant[], message: string }>}
 */
export async function fetchFilteredRestaurants(opts = {}) {
  const {
    distance = null,
    sort = null,
    isRestaurantOfTheDay = false,
    latitude,
    longitude,
    ...fetchInit
  } = opts || {};

  if (!distance) {
    throw new Error('Distance is required for filtered restaurant search');
  }

  const latOk = latitude !== undefined && latitude !== null && String(latitude).trim() !== '';
  const lngOk = longitude !== undefined && longitude !== null && String(longitude).trim() !== '';
  if (!latOk || !lngOk) {
    throw new Error('Latitude and longitude are required for distance filtering');
  }

  const qs = new URLSearchParams();
  const sortBy = mapDistanceFilterSortBy(sort);
  if (sortBy) qs.set('sort_by', sortBy);
  qs.set('lat', String(latitude));
  qs.set('lng', String(longitude));
  if (isRestaurantOfTheDay) {
    qs.set('restaurant_of_the_day', '1');
  }

  const path = `/restaurants/filter/${encodeURIComponent(String(distance))}`;
  const query = qs.toString();
  const json = await getJson(query ? `${path}?${query}` : path, fetchInit);

  if (json && json.success === false) {
    throw new Error(json.message || 'Filtered restaurants request was not successful');
  }

  const restaurants =
    (json && json.data && Array.isArray(json.data.restaurants) && json.data.restaurants) ||
    (json && json.data && Array.isArray(json.data.data) && json.data.data) ||
    (json && Array.isArray(json.restaurants) && json.restaurants) ||
    (json && Array.isArray(json.data) && json.data) ||
    null;

  if (!Array.isArray(restaurants)) {
    throw new Error('Unexpected API shape: restaurants is not an array');
  }

  return {
    restaurants,
    message: json?.message || '',
  };
}

/**
 * @deprecated Kept for older call sites; restaurant-of-the-day is no longer location-based.
 */
export const RESTAURANT_OF_THE_DAY_FILTER_DISTANCE = '100km';

/**
 * Loads restaurant(s) of the day from `GET /getRestaurants?is_restaurant_of_the_day=true` only.
 * Selection is **only** by the `is_restaurant_of_the_day` flag on the server — coordinates are not used.
 *
 * @param {RequestInit} [opts]
 * @returns {Promise<{ restaurants: Restaurant[], message: string }>}
 */
export async function fetchRestaurantsOfTheDay(opts = {}) {
  const fetchInit = { ...(opts || {}) };
  delete fetchInit.latitude;
  delete fetchInit.longitude;
  delete fetchInit.lat;
  delete fetchInit.lng;
  delete fetchInit.distance;
  delete fetchInit.sort;

  const { restaurants, message } = await fetchRestaurants({
    isRestaurantOfTheDay: true,
    ...fetchInit,
  });

  return {
    restaurants: narrowToRestaurantOfTheDay(restaurants),
    message,
  };
}

/**
 * Search restaurants by query (GET /searchRestaurants?query=...).
 *
 * API response includes `branches` per restaurant (addresses, coordinates, etc.)
 *
 * @param {string} query
 * @param {{
 *   latitude?: number|string|null,
 *   longitude?: number|string|null,
 *   radiusKm?: number|string|null, // omit for global search; when set, API limits to radius (km)
 * } & RequestInit} [opts]
 * @returns {Promise<{ restaurants: any[], message: string }>}
 */
export async function searchRestaurants(query, opts = {}) {
  const q = String(query || '').trim();
  if (!q) {
    return { restaurants: [], message: '' };
  }

  const { latitude, longitude, radiusKm, ...fetchInit } = opts || {};

  const qs = new URLSearchParams({ query: q });
  const latOk = latitude !== undefined && latitude !== null && String(latitude).trim() !== '';
  const lngOk = longitude !== undefined && longitude !== null && String(longitude).trim() !== '';
  if (latOk && lngOk) {
    qs.set('latitude', String(latitude));
    qs.set('longitude', String(longitude));
  }
  if (radiusKm !== undefined && radiusKm !== null && String(radiusKm).trim() !== '') {
    qs.set('radius_km', String(radiusKm));
  }

  const path = `/searchRestaurants?${qs.toString()}`;
  const json = await getJson(path, fetchInit);

  if (!json?.success) {
    throw new Error(json?.message || 'Search request was not successful');
  }

  const restaurants = json?.data?.restaurants;
  if (!Array.isArray(restaurants)) {
    throw new Error('Unexpected API shape: data.restaurants is not an array');
  }

  return {
    restaurants,
    message: json.message || '',
  };
}

/**
 * One row from GET /searchMenuItems.
 * @typedef {Object} SearchMenuItemHit
 * @property {string} id
 * @property {string} uuid
 * @property {string} item_name
 * @property {string} [description]
 * @property {string} [price]
 * @property {string} [preparation_time]
 * @property {string} [item_photo_url]
 * @property {string} branch_id
 * @property {{ id: number, name: string } | null} [restaurant]
 */

/**
 * Search menu items for one restaurant (GET /getRestaurantDetails/{id}/searchMenuItems?query=…).
 *
 * @param {string|number} restaurantId
 * @param {string} query
 * @param {{ locale?: string, limit?: number|string } & RequestInit} [opts]
 * @returns {Promise<{ items: SearchMenuItemHit[], message: string }>}
 */
export async function searchMenuItems(restaurantId, query, opts = {}) {
  const rid = encodeURIComponent(String(restaurantId ?? '').trim());
  if (!rid) {
    throw new Error('restaurantId is required to search menu items');
  }

  const q = String(query || '').trim();
  if (!q) {
    return { items: [], message: '' };
  }

  const { locale, limit, ...fetchInit } = opts || {};

  const qs = new URLSearchParams({ query: q });
  if (locale !== undefined && locale !== null && String(locale).trim() !== '') {
    qs.set('locale', String(locale));
  }
  if (limit !== undefined && limit !== null && String(limit).trim() !== '') {
    qs.set('limit', String(limit));
  }

  const path = `/getRestaurantDetails/${rid}/searchMenuItems?${qs.toString()}`;
  const json = await getJson(path, fetchInit);

  if (!json?.success) {
    throw new Error(json?.message || 'Menu item search was not successful');
  }

  const items = json?.data?.items;
  if (!Array.isArray(items)) {
    throw new Error('Unexpected API shape: data.items is not an array');
  }

  return {
    items,
    message: json.message || '',
  };
}

/**
 * Restaurant details (from GET /getRestaurantDetails/:idOrSlug).
 * @typedef {Object} RestaurantDetails
 * @property {string} id
 * @property {string} [slug]
 * @property {string} name
 * @property {string} [description]
 * @property {string} [logo_url]
 * @property {string} [dark_logo_url]
 * @property {string} [banner_url]
 * @property {string} [phone_number]
 * @property {string} [email]
 * @property {string} [address]
 * @property {RestaurantCuisine[]} [cuisines]
 */

/**
 * Menu (from GET /getRestaurantDetails/:id).
 * @typedef {Object} RestaurantMenu
 * @property {string} id
 * @property {string} uuid
 * @property {string} branch_id
 * @property {string|number} [order]
 * @property {{ en?: string, [k: string]: string }} [menu_name]
 */

/**
 * Menu item (from GET /getRestaurantDetails/:id).
 * @typedef {Object} RestaurantItem
 * @property {string} id
 * @property {string} uuid
 * @property {string} branch_id
 * @property {string} [item_name]
 * @property {string} [description]
 * @property {string} [price]
 * @property {string} [preparation_time]
 * @property {string[]} [category_ids]
 * @property {string[]} [menu_ids]
 * @property {string} [item_photo_url]
 * @property {{ id?: string, uuid?: string, category_name?: string, order?: string|number }} [category]
 * @property {{ locale?: string, item_name?: string, description?: string }[]} [translations]
 */

/**
 * Item category (from GET /getRestaurantDetails/:id).
 * @typedef {Object} RestaurantItemCategory
 * @property {string} id
 * @property {string} uuid
 * @property {string} [category_name]
 * @property {string|number} [order]
 */

/**
 * API envelope for GET /getRestaurantDetails/:id.
 * @typedef {Object} GetRestaurantDetailsResponse
 * @property {boolean} success
 * @property {string} message
 * @property {{ restaurant?: RestaurantDetails, menus?: RestaurantMenu[], categories?: RestaurantItemCategory[], items?: RestaurantItem[] }} data
 */

/**
 * Fetches restaurant details by slug or numeric id.
 * Endpoint (dev proxy): /api/v1/getRestaurantDetails/:idOrSlug
 *
 * @param {string} idOrSlug
 * @param {{
 *   latitude?: number|string|null,
 *   longitude?: number|string|null,
 * } & RequestInit} [opts]
 * @returns {Promise<{ restaurant: RestaurantDetails, menus: RestaurantMenu[], categories: RestaurantItemCategory[], items: RestaurantItem[], message: string }>}
 */
export async function fetchRestaurantDetails(idOrSlug, opts = {}) {
  const safeId = encodeURIComponent(String(idOrSlug || '').trim());
  if (!safeId) throw new Error('Missing restaurant slug or id');

  const { latitude, longitude, orderType, order_type, fulfillment, ...fetchInit } = opts || {};
  const qs = new URLSearchParams();
  const mode = String(orderType || order_type || fulfillment || '').trim().toLowerCase();
  if (mode === 'pickup' || mode === 'delivery') {
    qs.set('order_type', mode);
  }
  const latOk = latitude !== undefined && latitude !== null && String(latitude).trim() !== '';
  const lngOk = longitude !== undefined && longitude !== null && String(longitude).trim() !== '';
  if (latOk && lngOk) {
    qs.set('latitude', String(latitude));
    qs.set('longitude', String(longitude));
  }

  const detailPath = qs.toString()
    ? `/getRestaurantDetails/${safeId}?${qs.toString()}`
    : `/getRestaurantDetails/${safeId}`;

  /** @type {GetRestaurantDetailsResponse} */
  const json = await getJson(detailPath, fetchInit);

  if (!json.success) {
    throw new Error(json.message || 'Restaurant details request was not successful');
  }

  let restaurant = json.data?.restaurant;
  if (!restaurant || typeof restaurant !== 'object') {
    throw new Error('Unexpected API shape: data.restaurant is missing');
  }

  restaurant = normalizeRestaurantApiFields(restaurant);

  const menus = Array.isArray(json.data?.menus) ? json.data.menus : [];
  const categories = Array.isArray(json.data?.categories) ? json.data.categories : [];
  const items = Array.isArray(json.data?.items) ? json.data.items : [];

  return {
    restaurant,
    menus,
    categories,
    items,
    message: json.message,
  };
}

